import { useNavigate } from "react-router-dom";
import { ChevronLeft, Trash2, Minus, Plus, ShieldCheck, Heart, Package, Award, Video, Headphones, ArrowRight } from "lucide-react";
import { useCart } from "../context/CartContext";

const formatRub = (n) =>
  new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(n) + " ₽";

const discountPercent = (retail, sale) => {
  if (!retail || retail <= sale) return 0;
  return Math.round(((retail - sale) / retail) * 100);
};

export default function Cart() {
  const navigate = useNavigate();
  const { items, setQuantity, removeItem, clearCart, itemsCount, itemsTotal, retailTotal, discount } =
    useCart();

  const isEmpty = items.length === 0;
  const total = itemsTotal;

  return (
    <div className="min-h-[100dvh] bg-[#F5F0EB] flex flex-col" data-testid="cart-page">
      <div className="w-full max-w-[480px] mx-auto flex flex-col flex-1">
      <header className="px-5 pt-3 pb-3 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 flex items-center justify-center -ml-2"
          data-testid="cart-back"
        >
          <ChevronLeft size={24} className="text-[#1A1A1A]" strokeWidth={1.5} />
        </button>
        <div className="flex flex-col items-center">
          <h1 className="text-[20px] font-serif font-bold tracking-tight text-[#1A1A1A]">
            Корзина
          </h1>
          <p className="text-[12px] text-[#1A1A1A]/55 font-sans">
            {itemsCount} {itemsCount === 1 ? "товар" : itemsCount > 1 && itemsCount < 5 ? "товара" : "товаров"}
          </p>
        </div>
        <div className="w-10" />
      </header>

      {isEmpty ? (
        <div className="flex-1 flex flex-col items-center justify-center px-5 gap-4" data-testid="cart-empty">
          <p className="text-[#1A1A1A]/55 text-sm font-sans">Корзина пуста</p>
          <button
            onClick={() => navigate("/")}
            className="px-8 py-3 bg-[#1A1A1A] text-white text-sm font-sans font-medium rounded-full"
            data-testid="cart-empty-shop"
          >
            К акциям
          </button>
        </div>
      ) : (
        <>
          <div className="px-5 flex flex-col gap-3 pb-4" data-testid="cart-items">
            {items.map((it) => {
              const pct = discountPercent(it.retailPrice, it.salePrice);
              return (
                <div
                  key={`${it.productId}::${it.size || ""}`}
                  className="bg-white rounded-2xl p-3 flex gap-3 items-stretch"
                  data-testid={`cart-item-${it.productId}`}
                >
                  <div className="w-[80px] h-[100px] rounded-xl overflow-hidden bg-[#E8E3DE] shrink-0">
                    {it.image ? (
                      <img src={it.image} alt={it.productName} className="w-full h-full object-cover" />
                    ) : it.videoUrl ? (
                      <video src={`${it.videoUrl}#t=0.1`} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                    ) : null}
                  </div>
                  <div className="flex-1 flex flex-col justify-between min-w-0">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.18em] text-[#1A1A1A]/55 font-serif truncate font-semibold">
                        {it.brand}
                      </p>
                      <p className="text-[13px] text-[#1A1A1A] font-sans font-medium leading-tight truncate">
                        {it.productName}
                      </p>
                      {it.size && it.size !== "OS" && (
                        <p className="text-[11px] text-[#1A1A1A]/55 font-sans mt-0.5">
                          {it.size}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[15px] text-[#1A1A1A] font-sans font-bold">
                        {formatRub(it.salePrice)}
                      </span>
                      {pct > 0 && (
                        <>
                          <span className="text-[12px] text-[#1A1A1A]/40 font-sans line-through">
                            {formatRub(it.retailPrice)}
                          </span>
                          <span className="text-[10px] font-sans font-bold text-red-500 bg-red-500/10 px-1.5 py-0.5 rounded">
                            -{pct}%
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end justify-between">
                    <button
                      onClick={() => removeItem(it.productId, it.size)}
                      className="p-1 text-[#1A1A1A]/40 active:text-red-500"
                      data-testid={`cart-remove-${it.productId}`}
                    >
                      <Trash2 size={18} strokeWidth={1.5} />
                    </button>
                    <div className="flex items-center gap-2 bg-[#F5F0EB] rounded-full px-1 py-1">
                      <button
                        onClick={() => setQuantity(it.productId, it.size, it.quantity - 1)}
                        disabled={it.quantity <= 1}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-white disabled:opacity-30"
                        data-testid={`cart-dec-${it.productId}`}
                      >
                        <Minus size={14} strokeWidth={2} />
                      </button>
                      <span className="text-[13px] font-sans font-medium min-w-[14px] text-center">
                        {it.quantity}
                      </span>
                      <button
                        onClick={() => setQuantity(it.productId, it.size, it.quantity + 1)}
                        disabled={it.quantity >= 99}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-white disabled:opacity-30"
                        data-testid={`cart-inc-${it.productId}`}
                      >
                        <Plus size={14} strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div className="px-5 mt-auto">
            <div className="bg-white rounded-2xl p-4 mb-3">
              <div className="flex justify-between text-[13px] font-sans text-[#1A1A1A]/70 mb-1.5">
                <span>Сумма товаров</span>
                <span>{formatRub(retailTotal)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-[13px] font-sans text-red-500 mb-1.5">
                  <span>Скидка</span>
                  <span>-{formatRub(discount)}</span>
                </div>
              )}
              <div className="border-t border-[#1A1A1A]/8 my-2" />
              <div className="flex justify-between items-baseline">
                <span className="text-[14px] font-sans font-semibold text-[#1A1A1A]">Итого</span>
                <span className="text-[20px] font-sans font-bold text-[#1A1A1A]" data-testid="cart-total">
                  {formatRub(total)}
                </span>
              </div>
            </div>

            <button
              onClick={() => navigate("/checkout")}
              className="w-full bg-[#1A1A1A] text-white font-sans font-semibold py-4 rounded-full text-[15px] active:scale-[0.98] transition-transform flex items-center justify-center gap-2"
              data-testid="cart-checkout"
            >
              Оформить заказ
              <ArrowRight size={18} strokeWidth={2} />
            </button>

            <div className="flex items-center justify-center gap-1.5 mt-3 text-[#1A1A1A]/45 text-[11px] font-sans">
              <ShieldCheck size={13} strokeWidth={1.5} />
              <span>Безопасная оплата</span>
            </div>

            {/* Trust badges */}
            <div className="grid grid-cols-4 gap-2 mt-4 mb-6">
              <TrustBadge icon={<Package size={18} strokeWidth={1.4} />} text="Быстрая доставка из США" />
              <TrustBadge icon={<Award size={18} strokeWidth={1.4} />} text="100% оригинал гарантировано" />
              <TrustBadge icon={<Video size={18} strokeWidth={1.4} />} text="Живые обзоры из Mailano" />
              <TrustBadge icon={<Headphones size={18} strokeWidth={1.4} />} text="Мы всегда на связи" />
            </div>
          </div>
        </>
      )}
      </div>
    </div>
  );
}

function TrustBadge({ icon, text }) {
  return (
    <div className="flex flex-col items-center text-center gap-1.5">
      <span className="text-[#1A1A1A]/50">{icon}</span>
      <span className="text-[9px] font-sans text-[#1A1A1A]/50 leading-tight">{text}</span>
    </div>
  );
}
